import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';

interface ProfileRow {
  id: string;
  full_name: string;
}

interface PackageRow {
  id: string;
  student_id: string | null;
  name: string | null;
}

interface PaymentRow {
  id: string;
  package_id: string | null;
  student_id: string | null;
  amount: number | null;
  status: string;
  date: string | null;
  note: string | null;
}

interface OdemeModalProps {
  open: boolean;
  onClose: () => void;
  payment: PaymentRow | null;
  profiles: ProfileRow[];
  packages: PackageRow[];
}

export default function OdemeModal({ open, onClose, payment, profiles, packages }: OdemeModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<string>('');

  useEffect(() => {
    if (!open) {
      setError(null);
      setSaving(false);
    }
    setSelectedStudent(payment?.student_id ?? '');
  }, [open, payment]);

  const filteredPackages = packages.filter((p) => !selectedStudent || p.student_id === selectedStudent);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const student_id = (fd.get('student_id') as string || '').trim() || null;
    const package_id = (fd.get('package_id') as string || '').trim() || null;
    const amountStr = (fd.get('amount') as string || '').trim();
    const amount = amountStr ? Number(amountStr) : null;
    const status = (fd.get('status') as string || '').trim() || 'bekliyor';
    const date = (fd.get('date') as string || '').trim() || null;
    const note = (fd.get('note') as string || '').trim() || null;

    if (!student_id) {
      setError('Lütfen öğrenci seçin.');
      return;
    }
    if (amount === null || amount <= 0) {
      setError('Lütfen geçerli bir tutar girin.');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const payload = { student_id, package_id, amount, status, date, note };

      if (payment) {
        const { error: updateError } = await supabase
          .from('zehrahoca_payments')
          .update(payload)
          .eq('id', payment.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_payments')
          .insert(payload);
        if (insertError) throw insertError;
      }

      form.reset();
      window.dispatchEvent(new Event('zehrahoca:odeme-changed'));
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Ödeme kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={payment ? 'Ödeme Düzenle' : 'Yeni Ödeme Ekle'}
      subtitle={payment ? 'Ödeme bilgilerini güncelleyin.' : 'Yeni bir tahsilat kaydı oluşturun.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Öğrenci</label>
            <select
              name="student_id"
              defaultValue={payment?.student_id ?? ''}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Öğrenci seçin</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Tutar (₺)</label>
            <input
              type="number"
              name="amount"
              min={1}
              step="0.01"
              defaultValue={payment?.amount ?? ''}
              placeholder="1500"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Durum</label>
            <select
              name="status"
              defaultValue={payment?.status ?? 'odendi'}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="odendi">Ödendi</option>
              <option value="kismi">Kısmi Ödendi</option>
              <option value="bekliyor">Bekliyor</option>
              <option value="gecikti">Gecikti</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Tarih</label>
            <input
              type="date"
              name="date"
              defaultValue={payment?.date ?? new Date().toISOString().slice(0, 10)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">İlgili Paket</label>
            <select
              name="package_id"
              defaultValue={payment?.package_id ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Paket seçin</option>
              {filteredPackages.map((p) => (
                <option key={p.id} value={p.id}>{p.name ?? 'Paket'}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Not</label>
            <textarea
              name="note"
              defaultValue={payment?.note ?? ''}
              rows={2}
              maxLength={500}
              placeholder="Örn: Nakit tahsilat, havale..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap">İptal</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap">
            {saving ? 'Kaydediliyor...' : payment ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}